import './olc-file-select.scss';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { dialogActions } from '../slice/dialog-slice';
import { Button } from '../../ui/button/button';
import { apiActions } from '../../../slices/api-slice';
import folderOpen from '../../../assets/img/icon/folder-open-black.svg';
import folderClose from '../../../assets/img/icon/folder-close-black.svg';
import {
  ApiGetCloudFolderOrderResult,
  OrderResult,
} from '../../../api/front/cloud-folder/api-get-cloud-folder-order-result';
import { ResponseBase } from '../../../api/response-base';
import { useDidMount } from '../../../hooks/life-cycle';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { orderPreparationActions } from '../../../slices/order-preparation-slice';
import { ApiGetCloudFolderFileDownload } from '../../../api/cf/cloud-folder/api-get-cloud-folder-file-download';
import loading from '../../ui/image/loading.gif';
import { RefObject } from 'react';
import { EditableImage } from '../../../layout-editor/manager/image-edit/editable-image';
import { UiManager } from '../../../layout-editor/manager/ui/ui.manager';
import { EditableImageManager } from '../../../layout-editor/manager/editable-image/editable-image.manager';
import { ImageUploadStatus } from '../../ui/image-upload-status/image-upload-status';
import { StaticTooltip } from '../../ui/tooltip/static-tooltip';
import { Checkbox } from '../../ui/input/checkbox';
import { isCtrlOrCmdPressed } from '../../../utilities/is-ctrl-or-cmd-pressed';
import { Input } from '../../ui/input/input';
import { ApiImagesGet, ImagesGetResponse } from '../../../api/front/images/api-images';
import { useSelectChecker } from './hooks/use-select-checker';
import { ApiGetCloudFolderOrderFile, CloudFolderFileInfo, CloudFolderGetFileList } from '../../../api/front/cloud-folder/api-get-cloud-folder-order-file';
import { CfOrderGetOneDataEntity } from '../../../api/cf/api-cf-order-get-one/api-cf-order-get-one';
import fileThumbIcon from '../../../assets/img/icon/file_thumbnail.svg';
import { XmlStructureOrderInfoData } from '../../../xml/model/xml-structure-model';

type Props = {
  kijshopCd: string,
  shopOrderId: string,
  orderId: string,
  xml: XmlStructureOrderInfoData[],
  callback: (param: { 
    folderId: string,
    relativePath: string,
    name: string,
    files: CloudFolderFileInfo[],
  }) => void,
  isLayout?: boolean,
  orderData?: CfOrderGetOneDataEntity | null,
}

type HiddenComponentProps = {
  hidden: boolean,
  children?: React.ReactNode | React.ReactNode[],
}

const HiddenComponent = (props: HiddenComponentProps) => {
  const {
    hidden,
    children,
  } = props;

  if (hidden) return <></>;
  return (
    <>
      {children}
    </>
  );
};


type NestedFileDetail = {
  name: string;
  files: CloudFolderFileInfo[];
  children: NestedFileDetail[];
  relativePath: string;
};

const buildNestedStructure = (array: CloudFolderFileInfo[]): NestedFileDetail => {
  const root: NestedFileDetail = { name: '', files: [], children: [], relativePath: '' };
  array.forEach(file => {
    const pathParts = file.relativePath.split('/');
    pathParts.shift();
    let current = root;
    pathParts.forEach((part, index) => {
      let child = current.children.find(c => c.name === part);
      if (!child) {
        child = { name: part, files: [], children: [], relativePath: pathParts.slice(0, index + 1).join('/') };
        current.children.push(child);
      }
      current = child;
      if (index === pathParts.length - 1) {
        current.files.push(file);
      }
    });
  });
  return root;
};

type OrderNameProps = {
  name: string,
}
const OrderName = (props: OrderNameProps) => {
  const { name } = props;
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {name}
      {
        name.length >= 10 && hover ?
          <StaticTooltip
            messages={[name]}
            pos={{ x: '20', y: '20' }}
            style={{
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          : <></>
      }
    </div>
  );
};

const FolderView = (props: {
  index: number,
  folder: NestedFileDetail,
  select: string,
  onSelect: (v: string) => void,
  fileList: CloudFolderFileInfo[];
  _key: string,
  orderId: string,
}) => {
  const {
    index,
    folder,
    select,
    onSelect,
    _key,
    fileList,
    orderId,
  } = props;

  const [open, setOpen] = useState(true);

  const handlerClick = useCallback(() => {
    if (select === folder.relativePath) {
      setOpen(!open);
    } else {
      onSelect(folder.relativePath);
    }
  }, [open, select, onSelect, folder]);

  const [hover, setHover] = useState(false);

  return (
    <div
      className={'file_select__folder_wrap'}
      key={_key + 'child'}
    >
      <div style={{ paddingLeft: index * 10 }} className="file_select__folder_wrap__cnt">
        <div
          className={'file_select__list_area__folder_name ' + (select === folder.relativePath ? 'active' : '')}
          onClick={handlerClick}
        >
          <img
            src={open || !folder.children.length ? folderOpen : folderClose}
            alt=""
          />
          <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {folder.name}
            {
              folder.name.length >= 10 && hover ?
                <StaticTooltip
                  messages={[folder.name]}
                  pos={{ x: '20', y: '20' }}
                  style={{
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
                : <></>
            }
          </div>
        </div>
      </div>
      {
        open ?
          <div className={'folder_name__children'}>
            {
              folder.children.map((_folder: NestedFileDetail) => (
                <FolderView
                  index={index + 1}
                  folder={_folder}
                  fileList={fileList}
                  select={select}
                  onSelect={onSelect}
                  orderId={orderId}
                  key={`folder-nest-list-${_folder.relativePath}`}
                  _key={`folder-nest-list-${_folder.relativePath}`}
                />
              ))
            }
          </div>
          : <></>
      }
    </div>
  );
};

export const OlcFolderSelectDialog = (props: Props) => {
  const { kijshopCd, callback, shopOrderId, orderId, isLayout, orderData, xml } = props;
  const dispatch = useDispatch();
  const { usedCloudFolders } = useAppSelector((state) => ({ usedCloudFolders: state.orderPreparation.usedCloudFolders }));
  const [editableImageList, setEditableImageList] = useState<EditableImage[]>([]);
  const [orderResultList, setOrderResultList] = useState<CloudFolderFileInfo[]>([]);
  const [selectOrderResult, setSelectOrderResult] = useState<OrderResult>();
  const [_selectRelativePath, setSelectRelativePath] = useState<string>('');
  const [inputSearchValue, setInputSearchValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const imgCnt = useRef<HTMLDivElement>(null);
  const fileList = useRef<{ id: string, file: File }[]>([]);
  const selectFolderCheckState = useSelectChecker({ initial: true });
  const fileDetails: CloudFolderFileInfo[] = useMemo(() => {
    if (!selectOrderResult) return [];
    const parts = selectOrderResult.id.split('/');
    // selectOrderResult.id = `${folderId}/${最大3階層パス}`。先頭(folderId)を除いた3階層パスで絞り込む。
    const selectedPath = parts.slice(1).join('/');
    return orderResultList.filter(data => {
      // クラウドフォルダは最大3階層。先頭3階層まで見てサブフォルダを区別する。
      const threeLevel = (data.relativePath ?? '').split('/').slice(0, 3).join('/');
      return threeLevel === selectedPath;
    });
  }, [
    selectOrderResult,
    orderResultList,
  ]);
  const splitList: OrderResult[] = useMemo(() => {
    if (!orderData) return [];
    const resultMap: Record<string, OrderResult> = {};
    for (const data of orderResultList) {
      const parts = data.relativePath?.split('/') ?? [];
      if (parts.length < 2) continue;
      // クラウドフォルダは最大3階層。先頭2階層固定だと共通親の下のサブフォルダが1件に潰れて
      // 個別選択できないため、先頭3階層まで見てフォルダ単位で一覧化する。
      const threeLevelPath = parts.slice(0, 3).join('/');
      const folderId = `${data.folderId}/${threeLevelPath}`;
      if (!resultMap[folderId]) {
        resultMap[folderId] = {
          createdAt: '',
          updatedAt: '',
          folderId,
          kijshopCd,
          id: folderId,
          // 末端フォルダ名を表示名にする（2階層アップでも空にならないよう末端から取得）。
          name: parts.slice(0, 3).pop() ?? '',
        };
      }
    }
    const result = Object.values(resultMap);
    result.sort((a, b) => a.name > b.name ? 1 : -1);
    const parent: OrderResult[] = [];
    const children: OrderResult[] = [];
    for (const resultInfo of result) {
      const id = resultInfo.id.split('/')[0] ?? '';
      (id === orderData.uploadFolderId ? parent : children).push(resultInfo);
    }
    return parent.concat(children);
  }, [
    kijshopCd,
    orderResultList,
    orderData,
  ]);
  const folderNestList = useMemo(() => {
    return buildNestedStructure(fileDetails ?? []).children
  }, [fileDetails]);

  const isSingleFolder = useMemo(() => {
    return !!((folderNestList.length === 1) && !folderNestList[0]?.children.length);
  }, [folderNestList]);

  const selectRelativePath = useMemo(() => {
    if (isSingleFolder) return folderNestList[0].relativePath ?? '';
    return _selectRelativePath;
  }, [isSingleFolder, _selectRelativePath, folderNestList]);

  const _selectFolderImages = useMemo(() => {
    if (!orderResultList || !selectRelativePath) return [];
    return fileDetails.filter((v) => {
      const relativePath = v.relativePath.split('/');
      relativePath.shift();
      return relativePath.join('/') === selectRelativePath
    });
  }, [orderResultList, selectRelativePath, fileDetails]);

  const selectFolderImages = useMemo(() => {
    const isRootPath = (selectRelativePath?.split('/').filter((v) => !!v).length ?? -1) === 0;
    if (!_selectFolderImages.length && isRootPath) {
      return [...fileDetails].sort((a, b) => a.orgFilename.localeCompare(b.orgFilename, 'ja', { numeric: true }));
    }
    return [..._selectFolderImages].sort((a, b) => a.orgFilename.localeCompare(b.orgFilename, 'ja', { numeric: true }));
  }, [fileDetails, _selectFolderImages, selectRelativePath]);

  const handlerCLickClose = useCallback(() => {
    dispatch(dialogActions.pop());
  }, []);

  const viewOrderList = useMemo(() => {
    if (!searchValue) return splitList;
    const reg = new RegExp(searchValue.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const result = splitList.filter((v) => v.name.match(reg));
    if (selectOrderResult && !result.find((v) => v.id === selectOrderResult.id)) {
      setSelectOrderResult(undefined);
      setSelectRelativePath('');
    }
    return result;
  }, [splitList, selectOrderResult, searchValue]);

  const checkIsUseFolder = useCallback((
    folderId: string,
    relativePath: string,
    name: string,
  ) => {
    const found = Object.entries(usedCloudFolders).find(([id, entry]) => {
      if (id === orderId) return false;
      if (entry.folderId !== folderId || entry.relativePath !== relativePath || entry.name !== name) return false;
      const purposeId = xml.find((info) => info.xml.metaModel.id === id)?.xml.viewModel.purpose?.id;
      return !!purposeId;
    });
    if (!found) return undefined;
    const entry = found[1];
    return entry;
  }, [
    usedCloudFolders,
    orderId,
    xml,
  ]);

  const handleClickSubmit = useCallback(() => {
    if (!selectOrderResult) return;
    const [folderId, ...splitRelativePath] = selectOrderResult.folderId.split('/');
    if (!folderId || !splitRelativePath.length) return;
    // 選択したフォルダの最大3階層パスを保持する（表示・絞り込みと基準をそろえる）。
    const relativePath = splitRelativePath.slice(0, 3).join('/');
    const folderName = relativePath.split('/').pop() ?? '';
    const check = checkIsUseFolder(folderId, relativePath, folderName);
    if (check) {
      const cntStyle: React.CSSProperties = {
        border: '1px solid rgba(0, 0, 0, .4)',
        padding: '0.5rem 0.25rem',
        marginTop: '0.5rem',
        background: '#f7f7f7',
        gap: '0.25rem',
        display: 'flex',
        flexDirection: 'column',
      }
      const wrapStyle: React.CSSProperties = {
        display: 'flex',
      }
      const labelStyle: React.CSSProperties = {
        width: '56px',
      }
      dispatch(dialogActions.push({
        title: '確認',
        element: (
          <>
            <div className={'dialog__contents'}>
              <div>選択されたフォルダは既に使用されています。</div>
              <div
                style={cntStyle}
              >
                <div style={wrapStyle}>
                  <div style={labelStyle}>フォルダ</div>
                  <div>：{folderName}</div>
                </div>
                <div style={wrapStyle}>
                  <div style={labelStyle}>商品</div>
                  <div>：{check.goodsName}</div>
                </div>
              </div>
            </div>
            <div className={'dialog_contents__footer file_select_footer'}>
              <Button
                label={'OK'}
                onClick={() => dispatch(dialogActions.pop())}
              />
            </div>
          </>
        )
      }));
      return;
    }
    const goodsName = xml.find((info) => info.xml.metaModel.id === orderId)?.xml.viewModel.goods?.name ?? '';
    dispatch(orderPreparationActions.setUsedCloudFolder({
      orderId,
      entry: { folderId, relativePath, name: folderName, goodsName },
    }));
    callback({
      folderId,
      relativePath,
      name: folderName,
      files: selectFolderImages,
    });
  }, [
    callback,
    dispatch,
    xml,
    orderId,
    checkIsUseFolder,
    selectOrderResult,
    selectFolderImages,
  ]);
  const getOrderResult = useCallback(() => {
    dispatch(apiActions.run(
      new ApiGetCloudFolderOrderFile({ 
        kijshopCd,
        orderId: orderData?.id ?? '',
        kind: 1,

       }),
      {
        onSuccess: (res: ResponseBase<CloudFolderGetFileList>) => {
          setOrderResultList(res.body.data?.fileList ?? []);
        },
      },
    ));
  }, [
    dispatch,
    orderData,
    kijshopCd,
  ]);

  const getFileDetail = useCallback((orderResult: OrderResult) => {
    if (orderResult === selectOrderResult) return;
    setSelectOrderResult(orderResult);
  }, [
    selectOrderResult,
  ]);

  const handlerSelectOrder = useCallback((order: OrderResult) => {
    if (selectOrderResult === order) return;
    setSelectOrderResult(order);
    getFileDetail(order);
    setSelectRelativePath('');
    selectFolderCheckState.onSelect();
  }, [selectOrderResult, getFileDetail, selectFolderCheckState]);

  const handlerSelectFolder = useCallback((path: string) => {
    if (selectRelativePath === path) return;
    imgCnt.current?.scrollTo({ top: 0 });
    setSelectRelativePath(path);
  }, [selectRelativePath]);

  const onSearch = useCallback((val: string) => {
    setSearchValue(val)
  }, [])

  const handlerEnterKeyPressSearch = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter') && !e.nativeEvent.isComposing) {
      onSearch(inputSearchValue)
    }
  }, [inputSearchValue]);

  useEffect(() => {
    getOrderResult();
  }, []);
  return (
    <>
      <div className={'dialog__contents c_folder_select_dialog'}>
        <div className="search_cnt">
          <Input
            value={inputSearchValue}
            onChange={(e) => setInputSearchValue(e.target.value)}
            onKeyDown={handlerEnterKeyPressSearch}
          />
          <Button label="検索" onClick={() => onSearch(inputSearchValue)} />
        </div>
        <div className={'file_select'}>
          <div className={'file_select__list_area'}>
            {
              viewOrderList.map((item, i) => (
                <div
                  key={`order-result-${item.id}`}
                  onClick={() => {
                    handlerSelectOrder(item);
                  }}
                  className={'file_select__list_area__order_name ' + (selectOrderResult === item ? 'active' : '')}
                >
                  <img
                    src={selectOrderResult === item ? folderOpen : folderClose}
                    alt=""
                  />
                  <OrderName name={item.name} />
                </div>
              ))
            }
          </div>
          <div style={{ height: '100%', overflowY: 'hidden', paddingTop: '10px', width: '100%' }}>
            <div
              className={'file_select__test'}
              ref={imgCnt}
              style={{ height: 'calc(100% - 30px)' }}
            >
              <HiddenComponent hidden={!selectFolderImages.length}>
                {
                  ((!selectFolderImages.length && (selectRelativePath?.split('/').filter((v) => !!v).length ?? -1) === 0) ?
                  fileDetails
                    : selectFolderImages
                  ).map((item, i) => (
                    <ImageComponent
                      key={`file_test_${i}`}
                      data={item}
                      kijshopCd={kijshopCd}
                      orderId={selectOrderResult?.id ?? ''}
                      fileList={fileList}
                      editableImageList={editableImageList}
                    />
                  ))
                }
              </HiddenComponent>
            </div>
          </div>
        </div>
      </div>
      <div className={'dialog_contents__footer file_select_footer'}>
        <Button
          label={'閉じる'}
          onClick={handlerCLickClose}
          color={'light'}
        />
        <Button
          label={'選択'}
          onClick={handleClickSubmit}
          disabled={!selectOrderResult}
        />
      </div>
    </>
  );
};

type ImageProps = {
  data: CloudFolderFileInfo,
  kijshopCd: string,
  // CloudFolder OrderID
  orderId: string,
  fileList: RefObject<{ id: string, file: File }[]>,
  editableImageList: EditableImage[],
}

const ImageComponent = (props: ImageProps) => {
  const {
    data,
    kijshopCd,
    orderId,
    fileList,
    editableImageList,
  } = props;
  const dispatch = useAppDispatch();

  const [src, setSrc] = useState('');
  const init = useRef<boolean>(false);
  const cnt = useRef<HTMLDivElement>(null);
  const idRef = useRef(data.id);

  const editableImage = useMemo(() => {
    const target = editableImageList.find((v) => v.cfImageId === data.id);
    return target ?? null;
  }, [data, editableImageList]);

  useEffect(() => {
    idRef.current = data.id;
    const target = fileList.current?.find((info) => info.id === data.id);
    if (target) {
      const url = URL.createObjectURL(target.file);
      setSrc(url);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((v) => {
        if (v.isIntersecting && !init.current && data.pathThumb) {
          init.current = true;
          dispatch(apiActions.run(new ApiGetCloudFolderFileDownload({
            kijshopCd,
            orderId,
            path: `${data.pathThumb}/${data.orgFilename}`,
          }), {
            onSuccess: (res: Blob) => {
              const file = new File([res], data.orgFilename);
              fileList.current?.push({
                id: data.id,
                file,
              });
              if (idRef.current === data.id) {
                const url = URL.createObjectURL(res);
                setSrc(url);
              }
            },
          }));
        }
      });
    });
    if (cnt.current) {
      observer.observe(cnt.current);
    }


    return () => {
      observer.disconnect();
      init.current = false;
      setSrc('');
      if (src) {
        URL.revokeObjectURL(src);
      }
    };
  }, [data]);

  return (
    <div
      className={`image ${!src ? 'loading' : ''}`}
      ref={cnt}
    >
      <div className="image__wrap">
        <img
          src={data.pathThumb ? src || loading : fileThumbIcon}
          alt=""
        />
        {editableImage && <ImageUploadStatus
          uploaded={editableImage.flags.uploaded && !editableImage.isDelete}
          uploading={editableImage.flags.uploading}
        />}
      </div>
      <label>{data.orgFilename}</label>
    </div>
  );
};
